import React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { House, ClockCounterClockwise, ChartBar, Info } from 'phosphor-react';

const LOGO_HORIZONTAL = "https://customer-assets.emergentagent.com/job_chart-analyzer-107/artifacts/r9xkm2vj_cerebro%20%2B%20genilateral.png";

/**
 * SharedProfileNavigation
 * 
 * Navigation bar for both:
 * - Legacy shared profiles: /shared/{shareId}/...
 * - New public profiles: /profile/{userId}/...
 * 
 * PUBLIC PROFILE NAVIGATION (Simplified):
 * - Overview (was Dashboard)
 * - Analytics
 * - Trades (was History)
 * - About
 * 
 * REMOVED from public view:
 * - Positions (moved to separate premium feature)
 * - Alerts (user-specific functionality)
 * - Advanced (consolidated into Analytics)
 * - Performance (consolidated into Analytics)
 * 
 * Props:
 * - isPublicProfile: boolean - if true, use /profile/{userId}/ paths
 * - profileUserId: string - the userId for public profile mode
 */
export default function SharedProfileNavigation({ isPublicProfile = false, profileUserId = null }) {
  const { shareId, userId: routeUserId } = useParams();
  const location = useLocation();
  
  // Determine the base path and identifier based on mode
  // In public profile mode, prefer props over route params for stability
  const effectiveUserId = profileUserId || routeUserId;
  
  // Guard: In public profile mode, we MUST have a userId
  // In shared mode, we MUST have a shareId
  const basePath = isPublicProfile 
    ? `/profile/${effectiveUserId || 'unknown'}` 
    : `/shared/${shareId || 'unknown'}`;
  
  // Additional safety: if required identifier is missing, log warning (dev only)
  if (process.env.NODE_ENV !== 'production') {
    if (isPublicProfile && !effectiveUserId) {
      console.warn('[SharedProfileNavigation] Public profile mode but no userId available');
    }
    if (!isPublicProfile && !shareId) {
      console.warn('[SharedProfileNavigation] Shared mode but no shareId available');
    }
  }
  
  // PUBLIC PROFILE NAVIGATION ITEMS
  // Order: Overview → Analytics → Trades → About
  // REMOVED: Positions (separate premium feature), Alerts, Advanced, Performance
  const navItems = [
    { path: `${basePath}/dashboard`, label: 'Overview', icon: House },
    { path: `${basePath}/analytics`, label: 'Analytics', icon: ChartBar },
    { path: `${basePath}/history`, label: 'Trades', icon: ClockCounterClockwise },
    // Positions REMOVED from public navigation - will be a separate premium feature
    { path: `${basePath}/about`, label: 'About', icon: Info },
  ];

  return (
    // Position at top since banner is removed (was top-[45px])
    <nav className="fixed top-0 left-0 right-0 z-40 border-b border-white/10 bg-[#0A0A0A]" data-testid="shared-profile-navigation">
      <div className="mx-auto max-w-[1600px] px-4 md:px-8">
        <div className="flex h-16 items-center justify-between gap-4">
          {/* Logo */}
          <Link to={`${basePath}/dashboard`} className="flex flex-shrink-0 items-center leading-none" data-testid="shared-profile-navigation-logo-link">
            <img src={LOGO_HORIZONTAL} alt="GENI" className="h-10 w-auto object-contain md:h-11 lg:h-12" data-testid="shared-profile-navigation-logo" />
          </Link>

          {/* Navigation Links */}
          <div className="flex items-center gap-1 overflow-x-auto" data-testid="shared-profile-navigation-links">
            {navItems.map(item => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`shared-profile-navigation-link-${item.label.toLowerCase()}`}
                  className={`flex items-center gap-2 px-3 py-2 rounded-sm text-sm font-medium transition-colors whitespace-nowrap ${
                    isActive
                      ? 'bg-[#00E676]/10 text-[#00E676]'
                      : 'text-white/60 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <Icon size={18} weight={isActive ? 'bold' : 'regular'} />
                  <span className="hidden sm:inline">{item.label}</span>
                </Link>
              );
            })}
          </div>

          {/* Empty space for balance */}
          <div className="hidden w-[132px] flex-shrink-0 md:block" aria-hidden="true" />
        </div>
      </div>
    </nav>
  );
}
